import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { lcApiBase, lcHttpError, lcStationId } from './logiciel-caisse-api.helper';
import { LogicielCaisseAuthService } from './logiciel-caisse-auth.service';

/** Ligne renvoyée par l'API (table `produit`). */
export interface ProduitItem {
  id: number;
  user_id: number;
  station_id: number;
  nom: string;
  reference_interne?: string | null;
  reference_fabricant?: string | null;
  code_barre?: string | null;
  description?: string | null;
  prix_achat?: number | null;
  prix_vente?: number | null;
  quantite?: number;
  actif?: boolean;
  categorie_libelle?: string | null;
  image?: string | null;
  code_art?: string | null;
  couleur?: string | null;
  taille?: string | null;
  rayon?: string | null;
  famille?: string | null;
  sous_famille?: string | null;
  param1?: string | null;
  param2?: string | null;
  param3?: string | null;
  param4?: string | null;
  param5?: string | null;
  param6?: string | null;
  param7?: string | null;
  param8?: string | null;
  param9?: string | null;
  param10?: string | null;
  param11?: string | null;
  param12?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  stock_physique_global?: number | null;
  stock_disponible_global?: number | null;
  reasort_min_stock?: number | null;
  reasort_max_stock?: number | null;
  [key: string]: unknown;
}

export interface ProduitsPaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface ProduitsPageResult {
  items: ProduitItem[];
  pagination: ProduitsPaginationMeta;
}

export interface ProduitTransfertVariant {
  product_id: number;
  code_barre: string;
  nom: string;
  couleur?: string;
  taille?: string;
  stock_disponible_depart?: number | null;
}

export interface ProduitTransfertSuggestionGroup {
  group_key: string;
  reference: string;
  libelle: string;
  variants: ProduitTransfertVariant[];
}

export interface ProduitCsvImportLineError {
  line: number;
  error: string;
}

export interface ProduitCsvImportResult {
  created: number;
  updated: number;
  failed: number;
  errors: ProduitCsvImportLineError[];
  message: string;
}

interface TransfertSuggestionsResponse {
  success: boolean;
  groups?: ProduitTransfertSuggestionGroup[];
  error?: string;
}

interface ProduitsListResponse {
  success: boolean;
  produits?: ProduitItem[];
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
  meta?: {
    total?: number;
    page?: number;
    limit?: number;
    pages?: number;
    nb_total_pages?: number;
  };
  error?: string;
}

interface ProductsCountsResponse {
  success: boolean;
  actif?: number;
  archive?: number;
  error?: string;
}

interface UpdateProduitResponse {
  success: boolean;
  produit?: ProduitItem;
  message?: string;
  error?: string;
}

interface ProduitCsvImportResponse {
  success: boolean;
  created?: number;
  updated?: number;
  failed?: number;
  errors?: ProduitCsvImportLineError[];
  message?: string;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class LogicielCaisseProduitService {
  constructor(
    private readonly http: HttpClient,
    private readonly authService: LogicielCaisseAuthService
  ) {}

  getProductsCounts(): Observable<{ actif: number; archive: number }> {
    let stationId: number;
    try {
      stationId = lcStationId(this.authService);
    } catch (e) {
      return throwError(() => (e instanceof Error ? e : new Error(String(e))));
    }
    const url = `${lcApiBase(this.authService)}/api/products/counts/${stationId}`;
    return this.http.get<ProductsCountsResponse>(url).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error(res?.error || 'Réponse compteurs produits invalide');
        }
        return {
          actif: res.actif ?? 0,
          archive: res.archive ?? 0,
        };
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement des compteurs produits.'))
      )
    );
  }

  getProduits(options: {
    actif: boolean;
    page?: number;
    limit?: number;
    byGroup?: boolean;
    q?: string;
    taille?: string;
    couleur?: string;
    prix?: number;
    prixOp?: string;
    stockState?: 'in_stock' | 'low_stock' | 'out_of_stock';
    entrepotIds?: number[];
    stockPhysMin?: number;
    stockPhysMax?: number;
  }): Observable<ProduitsPageResult> {
    let stationId: number;
    try {
      stationId = lcStationId(this.authService);
    } catch (e) {
      return throwError(() => (e instanceof Error ? e : new Error(String(e))));
    }

    let params = new HttpParams();
    const page = options.page ?? 1;
    const limit = options.limit ?? 10;
    params = params.set('actif', options.actif ? 'true' : 'false');
    params = params.set('page', String(Math.max(1, page)));
    params = params.set('limit', String(Math.min(100, Math.max(1, limit))));
    if (options.byGroup) {
      params = params.set('by_group', 'true');
    }

    const kw = String(options.q ?? '').trim();
    if (kw.length > 0) {
      params = params.set('q', kw.slice(0, 120));
    }
    const taille = String(options.taille ?? '').trim();
    if (taille) {
      params = params.set('taille', taille);
    }
    const couleur = String(options.couleur ?? '').trim();
    if (couleur) {
      params = params.set('couleur', couleur);
    }
    if (options.prix != null && Number.isFinite(options.prix)) {
      params = params.set('prix', String(options.prix));
      const op = String(options.prixOp ?? '=').trim() || '=';
      params = params.set('prix_op', op);
    }
    if (options.stockState) {
      params = params.set('stock_state', options.stockState);
    }
    const entIds = (options.entrepotIds ?? []).filter((n) => Number.isFinite(n) && n > 0);
    if (entIds.length > 0) {
      params = params.set('entrepot_ids', entIds.join(','));
    }
    if (options.stockPhysMin != null && Number.isFinite(options.stockPhysMin)) {
      params = params.set('stock_phys_min', String(options.stockPhysMin));
    }
    if (options.stockPhysMax != null && Number.isFinite(options.stockPhysMax)) {
      params = params.set('stock_phys_max', String(options.stockPhysMax));
    }

    const url = `${lcApiBase(this.authService)}/api/products/station/${stationId}`;
    return this.http.get<ProduitsListResponse>(url, { params }).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.produits)) {
          throw new Error(res?.error || 'Réponse produits invalide');
        }
        const total = res.meta?.total ?? res.total ?? res.produits.length;
        const pageRes = res.meta?.page ?? res.page ?? page;
        const limitRes = res.meta?.limit ?? res.limit ?? limit;
        const pagesRaw =
          res.meta?.pages ??
          res.meta?.nb_total_pages ??
          res.totalPages ??
          Math.max(1, Math.ceil(total / limitRes));
        return {
          items: res.produits,
          pagination: {
            page: pageRes,
            limit: limitRes,
            total,
            totalPages: pagesRaw,
          },
        };
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement des produits.'))
      )
    );
  }

  importProduitsCsv(csvText: string): Observable<ProduitCsvImportResult> {
    const url = `${lcApiBase(this.authService)}/api/products/import/csv`;
    return this.http.post<ProduitCsvImportResponse>(url, { csv: csvText }).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error(res?.error || 'Import CSV produits refusé');
        }
        return {
          created: res.created ?? 0,
          updated: res.updated ?? 0,
          failed: res.failed ?? 0,
          errors: res.errors ?? [],
          message: res.message ?? '',
        };
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, "Erreur lors de l'import CSV produits."))
      )
    );
  }

  updateProduit(
    produitId: number,
    body: Record<string, unknown>
  ): Observable<ProduitItem | undefined> {
    const url = `${lcApiBase(this.authService)}/api/products/${produitId}`;
    return this.http.put<UpdateProduitResponse>(url, body).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error(res?.error || 'Mise à jour du produit impossible');
        }
        return res.produit;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de la mise à jour du produit.'))
      )
    );
  }

  setProduitActif(produitId: number, actif: boolean): Observable<ProduitItem | undefined> {
    const url = `${lcApiBase(this.authService)}/api/products/${produitId}`;
    return this.http.put<UpdateProduitResponse>(url, { actif }).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error(res?.error || 'Mise à jour du produit impossible');
        }
        return res.produit;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de la mise à jour du produit.'))
      )
    );
  }

  mapProduitToTableRow(
    p: ProduitItem,
    opts?: { includeReasort?: boolean }
  ): Record<string, string | unknown> {
    const refFab =
      p.reference_fabricant != null && String(p.reference_fabricant).trim() !== ''
        ? String(p.reference_fabricant).trim()
        : '—';
    const row: Record<string, string | unknown> = {
      id: String(p.id),
      REFERENCE: refFab,
      'CODE BARRE': this.formatCodeBarreCell(p.code_barre),
      DESIGNATION: p.nom?.trim() || '—',
      'PRIX DE VENTE': this.formatPrixVente(p.prix_vente),
      FAMILLE: this.familleDisplay(p),
      PARAM1: this.paramCell(p.param1),
      PARAM2: this.paramCell(p.param2),
      PARAM3: this.paramCell(p.param3),
      PARAM4: this.paramCell(p.param4),
      children: [],
    };
    if (p.stock_physique_global != null || p.stock_disponible_global != null) {
      row['STOCK PHYSIQUE'] = this.formatStockQty(p.stock_physique_global ?? 0);
      row['STOCK DISPONIBLE'] = this.formatStockQty(p.stock_disponible_global ?? 0);
    }
    if (opts?.includeReasort) {
      row['STOCK MIN'] = this.formatReasortStockCell(p.reasort_min_stock);
      row['STOCK MAX'] = this.formatReasortStockCell(p.reasort_max_stock);
    }
    return row;
  }

  mapProduitVariationRow(
    p: ProduitItem,
    opts?: { includeReasort?: boolean }
  ): Record<string, string | unknown> {
    const base = this.mapProduitToTableRow(p, opts);
    const nom = p.nom?.trim() || '—';
    const sous = this.variationSousDesignation(p);
    const designation = sous !== '' ? `${nom}\n${sous}` : nom;
    return {
      ...base,
      DESIGNATION: designation,
    };
  }

  groupProduitsForTable(
    items: ProduitItem[],
    opts?: { includeReasort?: boolean }
  ): Record<string, string | unknown>[] {
    const filtered = items.filter((p) => !this.isProduitCodeBarreZero(p));

    const byKey = new Map<string, ProduitItem[]>();
    const keyOrder: string[] = [];

    for (const p of filtered) {
      const fab = p.reference_fabricant;
      const key =
        fab != null && String(fab).trim() !== ''
          ? `fab:${String(fab).trim()}`
          : `solo:${p.id}`;
      if (!byKey.has(key)) {
        byKey.set(key, []);
        keyOrder.push(key);
      }
      byKey.get(key)!.push(p);
    }

    const out: Record<string, string | unknown>[] = [];
    for (const key of keyOrder) {
      const group = byKey.get(key)!;
      if (group.length === 1) {
        out.push(this.mapProduitToTableRow(group[0], opts));
        continue;
      }
      const sorted = [...group].sort((a, b) =>
        String(a.code_barre ?? '').localeCompare(String(b.code_barre ?? ''), 'fr', {
          numeric: true,
          sensitivity: 'base',
        })
      );
      const first = sorted[0];
      const refFab =
        first.reference_fabricant != null && String(first.reference_fabricant).trim() !== ''
          ? String(first.reference_fabricant).trim()
          : '—';
      const hasStock = sorted.some(
        (x) => x.stock_physique_global != null || x.stock_disponible_global != null
      );
      const parentRow: Record<string, string | unknown> = {
        id: String(first.id),
        isGroupParent: true,
        REFERENCE: refFab,
        'CODE BARRE': this.formatCodeBarreCell(first.code_barre),
        DESIGNATION: first.nom?.trim() || '—',
        'PRIX DE VENTE': this.formatPrixVente(first.prix_vente),
        FAMILLE: this.familleDisplay(first),
        PARAM1: this.paramCell(first.param1),
        PARAM2: this.paramCell(first.param2),
        PARAM3: this.paramCell(first.param3),
        PARAM4: this.paramCell(first.param4),
        children: sorted.map((ch) => this.mapProduitVariationRow(ch, opts)),
      };
      if (opts?.includeReasort) {
        parentRow['STOCK MIN'] = this.formatReasortStockCell(first.reasort_min_stock);
        parentRow['STOCK MAX'] = this.formatReasortStockCell(first.reasort_max_stock);
      }
      if (hasStock) {
        const sp = sorted.reduce((a, x) => a + Number(x.stock_physique_global ?? 0), 0);
        const sd = sorted.reduce((a, x) => a + Number(x.stock_disponible_global ?? 0), 0);
        parentRow['STOCK PHYSIQUE'] = this.formatStockQty(sp);
        parentRow['STOCK DISPONIBLE'] = this.formatStockQty(sd);
      }
      out.push(parentRow);
    }
    return out;
  }

  formatStockQty(v: number | null | undefined): string {
    if (v == null || Number.isNaN(Number(v))) {
      return '—';
    }
    const n = Number(v);
    return n.toLocaleString('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    });
  }

  getTransfertSuggestions(
    entrepotDepartId?: number | null
  ): Observable<ProduitTransfertSuggestionGroup[]> {
    const url = `${lcApiBase(this.authService)}/api/products/transfert-suggestions`;
    let params = new HttpParams();
    if (
      entrepotDepartId != null &&
      Number.isFinite(Number(entrepotDepartId)) &&
      Number(entrepotDepartId) > 0
    ) {
      params = params.set('entrepot_depart_id', String(entrepotDepartId));
    }
    return this.http.get<TransfertSuggestionsResponse>(url, { params }).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.groups)) {
          throw new Error(res?.error || 'Réponse suggestions invalide');
        }
        return res.groups;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur chargement références produits.'))
      )
    );
  }

  private variationSousDesignation(p: ProduitItem): string {
    const parts: string[] = [];
    const c =
      p.couleur != null && String(p.couleur).trim() !== '' ? String(p.couleur).trim() : '';
    const t =
      p.taille != null && String(p.taille).trim() !== '' ? String(p.taille).trim() : '';
    if (c) {
      parts.push(`Couleur : ${c}`);
    }
    if (t) {
      parts.push(`Taille : ${t}`);
    }
    return parts.join('\n');
  }

  private isProduitCodeBarreZero(p: ProduitItem): boolean {
    const cb = p.code_barre;
    if (cb == null) {
      return false;
    }
    const s = String(cb).trim();
    if (s === '') {
      return false;
    }
    const n = Number(String(s).replace(',', '.'));
    return !Number.isNaN(n) && n === 0;
  }

  private paramCell(v: string | null | undefined): string {
    if (v == null || String(v).trim() === '') {
      return '—';
    }
    return String(v).trim();
  }

  private formatCodeBarreCell(v: string | null | undefined): string {
    if (v == null || String(v).trim() === '') {
      return '';
    }
    return String(v).trim();
  }

  private familleDisplay(p: ProduitItem): string {
    const familleLib =
      p.famille != null && String(p.famille).trim() !== ''
        ? String(p.famille).trim()
        : p.categorie_libelle != null && String(p.categorie_libelle).trim() !== ''
          ? String(p.categorie_libelle).trim()
          : '';
    return familleLib !== '' ? familleLib : '—';
  }

  private formatReasortStockCell(v: number | null | undefined): string {
    if (v == null || Number.isNaN(Number(v))) {
      return '—';
    }
    return Number(v).toLocaleString('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }

  private formatPrixVente(v: number | null | undefined): string {
    if (v == null || Number.isNaN(Number(v))) {
      return '—';
    }
    const n = Number(v);
    return `${n.toLocaleString('fr-FR', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    })} TND`;
  }
}
